import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

const fmt = (secs) => {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
};

const TimerChip = ({ preset }) => {
  const [timeLeft, setTimeLeft] = useState(preset);
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);
  const ref = useRef(null);

  const isDone = timeLeft <= 0;
  const isUrgent = timeLeft <= 120 && timeLeft > 0;

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (running && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { setRunning(false); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const reset = useCallback(() => {
    clearInterval(intervalRef.current);
    setRunning(false);
    setTimeLeft(preset);
  }, [preset]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const chipClass = isDone
    ? 'bg-brand-orange text-white animate-pulse'
    : isUrgent
    ? 'bg-brand-orange text-white'
    : 'bg-ink text-white';

  return (
    <div ref={ref} className="relative shrink-0" data-testid="timer-chip">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="timer-display"
        className={`flex items-center gap-2.5 px-5 py-2 rounded-full font-mono font-bold text-2xl tracking-wider select-none transition-colors ${chipClass}`}
        title="Click to start / pause / reset"
      >
        {running && !isDone && (
          <span className="w-2 h-2 rounded-full bg-green-400 shrink-0 animate-pulse" />
        )}
        {fmt(timeLeft)}
      </button>

      {open && (
        <div
          data-testid="timer-controls"
          className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-xl border border-black/[0.07] p-4 flex items-center gap-3 z-50"
        >
          <button
            onClick={() => setRunning((r) => !r)}
            data-testid="timer-play-pause-btn"
            className="w-11 h-11 rounded-full bg-brand-orange flex items-center justify-center hover:bg-brand-orange-hover transition-colors"
          >
            {running
              ? <Pause size={18} className="text-white" strokeWidth={2} />
              : <Play size={18} className="text-white ml-0.5" strokeWidth={2} />}
          </button>
          <button
            onClick={reset}
            data-testid="timer-reset-btn"
            className="w-11 h-11 rounded-full border-2 border-black/10 flex items-center justify-center hover:border-brand-orange transition-colors"
          >
            <RotateCcw size={16} className="text-ink-secondary" />
          </button>
          <span className="text-xs text-ink-muted font-medium whitespace-nowrap">
            {isDone ? "Time's up!" : running ? 'Running…' : 'Paused'}
          </span>
        </div>
      )}
    </div>
  );
};

export default TimerChip;
